import React, { useEffect, useState } from "react";
import Layout from "../../components/layout/Layout";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { APIURL } from "../../redux/action/config";
import ServiceCatalogFormModal from "../../components/modals/ServiceCatalogFormModal";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import loader from "../../components/loader/loader";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
};

const ServiceCatalogList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${APIURL}/api/v1/service-catalog`,
        authHeaders(),
      );
      setItems(res?.data?.data?.serviceCatalogItems || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAddModal = () => {
    setEditingItem(null);
    setShowFormModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setShowFormModal(true);
  };

  const handleSubmit = async (form) => {
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      price: form.price === "" ? 0 : Number(form.price),
      priceNote: form.priceNote,
      isActive: form.isActive,
    };
    try {
      if (editingItem) {
        await axios.put(
          `${APIURL}/api/v1/service-catalog/${editingItem._id}`,
          payload,
          authHeaders(),
        );
        toast.success("Service updated successfully!");
      } else {
        await axios.post(
          `${APIURL}/api/v1/service-catalog`,
          payload,
          authHeaders(),
        );
        toast.success("Service added successfully!");
      }
      setShowFormModal(false);
      fetchItems();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (item) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${APIURL}/api/v1/service-catalog/${deletingItem._id}`,
        authHeaders(),
      );
      toast.success("Service deleted successfully!");
      setShowDeleteModal(false);
      fetchItems();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete service");
    } finally {
      setDeleting(false);
    }
  };

  const categories = Array.from(
    new Set(items.map((item) => item.category).filter(Boolean)),
  ).sort();

  const filteredItems = items.filter((item) => {
    const matchesSearch = item.name
      ?.toLowerCase()
      .includes(searchTerm.trim().toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" ? item.isActive !== false : item.isActive === false);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const groupedByCategory = filteredItems.reduce((acc, item) => {
    const key = item.category || "Uncategorized";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const handleResetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setStatusFilter("");
  };

  return (
    <React.Fragment>
      <Layout title="Service Catalog" page="servicecatalog">
        <div className="content d-flex flex-column flex-column-fluid" id="kt_content">
          <div id="kt_content_container" className="container-fluid">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title">Service Catalog</h3>
                <button className="btn btn-primary" onClick={openAddModal}>
                  Add Service
                </button>
              </div>
              <div className="card-body">
                <div className="row mb-5 g-3">
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by service name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <button
                      className="btn btn-light-secondary w-100"
                      onClick={handleResetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>
                {loading ? (
                  <div className="text-center p-5">{loader()}</div>
                ) : items.length === 0 ? (
                  <p>No services found.</p>
                ) : filteredItems.length === 0 ? (
                  <p>No services match your search/filter.</p>
                ) : (
                  Object.keys(groupedByCategory)
                    .sort()
                    .map((category) => (
                      <div key={category} className="mb-6">
                        <h4 className="mb-3">{category}</h4>
                        <div className="table-responsive">
                          <table className="table table-striped table-row-bordered gy-5 gs-7 border rounded">
                            <thead>
                              <tr className="fw-bolder fs-6 text-gray-800">
                                <th>Name</th>
                                <th style={{ width: "120px" }}>Price</th>
                                <th style={{ width: "160px" }}>Price Note</th>
                                <th style={{ width: "90px" }}>Active</th>
                                <th style={{ width: "150px" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupedByCategory[category].map((item) => (
                                <tr key={item._id}>
                                  <td>{item.name}</td>
                                  <td>${parseFloat(item.price || 0).toFixed(2)}</td>
                                  <td>{item.priceNote}</td>
                                  <td>
                                    <span
                                      className={`btn ${
                                        item.isActive !== false
                                          ? "btn-success"
                                          : "btn-warning"
                                      } text-capitalize`}
                                    >
                                      {item.isActive !== false ? "Active" : "Inactive"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center gap-1">
                                      <button
                                        onClick={() => openEditModal(item)}
                                        className="btn btn-info action-hover m-0"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          className="bi bi-pencil-square"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                                          <path
                                            fillRule="evenodd"
                                            d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                                          />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => confirmDelete(item)}
                                        className="btn btn-bg-dark action-hover m-0"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          className="bi bi-trash-fill"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>
      <ServiceCatalogFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleSubmit}
        editingItem={editingItem}
        isLoading={saving}
        categories={categories}
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Service"
        message={`Are you sure you want to delete "${deletingItem?.name}"?`}
        confirmText="Yes, Delete"
        confirmButtonClass="btn-danger"
        isLoading={deleting}
      />
      <ToastContainer />
    </React.Fragment>
  );
};

export default ServiceCatalogList;
